import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { 
  Eye, 
  EyeOff, 
  ChevronDown, 
  ChevronRight, 
  Copy, 
  ExternalLink,
  Clock,
  User,
  Activity
} from 'lucide-react';

const LogViewer = ({ log, expanded = false, onToggleExpanded }) => {
  const [showRawData, setShowRawData] = useState(false);
  const [copied, setCopied] = useState(false);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'text-red-600 bg-red-50 border-red-200';
      case 'HIGH': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'LOW': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'CREATE': return '➕';
      case 'UPDATE': return '✏️';
      case 'DELETE': return '🗑️';
      case 'VIEW': return '👁️';
      case 'EXPORT': return '📤';
      case 'BULK_UPDATE': return '📝';
      case 'VALIDATION_ERROR': return '⚠️';
      case 'SYNC': return '🔄';
      default: return '📝';
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(log, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatChanges = (changes) => {
    if (!changes || !changes.fieldsChanged || changes.fieldsChanged.length === 0) {
      return 'No field changes detected';
    }

    return changes.fieldsChanged.map(field => {
      const before = changes.before?.[field];
      const after = changes.after?.[field];
      
      return (
        <div key={field} className="mb-2 p-2 bg-gray-50 rounded border-l-4 border-blue-300">
          <div className="font-medium text-gray-700 mb-1">{field}</div>
          <div className="text-sm space-y-1">
            {before !== undefined && (
              <div className="flex items-start">
                <span className="text-red-600 font-medium mr-2">Before:</span>
                <span className="text-red-700">{String(before) || '(empty)'}</span>
              </div>
            )}
            {after !== undefined && (
              <div className="flex items-start">
                <span className="text-green-600 font-medium mr-2">After:</span>
                <span className="text-green-700">{String(after) || '(empty)'}</span>
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
      {/* Header - Always Visible */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggleExpanded}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            {/* Action Icon */}
            <div className="text-lg" title={log.action}>
              {getActionIcon(log.action)}
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="text-sm font-medium text-gray-900 truncate">
                  {log.action} on {log.resource}
                  {log.resourceId && ` (ID: ${log.resourceId})`}
                </h3>
                <span className={`px-2 py-1 text-xs font-medium rounded border ${getSeverityColor(log.severity)}`}>
                  {log.severity}
                </span>
              </div>

              <div className="flex items-center space-x-4 text-xs text-gray-500">
                <div className="flex items-center space-x-1">
                  <User className="w-3 h-3" />
                  <span>{log.userEmail}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>{formatDistanceToNow(log.timestamp.toDate(), { addSuffix: true })}</span>
                </div>
                {log.metadata?.source && (
                  <div className="flex items-center space-x-1">
                    <Activity className="w-3 h-3" />
                    <span>{log.metadata.source}</span>
                  </div>
                )}
              </div>

              {/* Quick Summary */}
              {log.changes?.fieldsChanged && log.changes.fieldsChanged.length > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  Changed: {log.changes.fieldsChanged.join(', ')}
                </div>
              )}
            </div>
          </div>

          {/* Expand/Collapse Button */}
          <button className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors">
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          <div className="space-y-4">
            {/* Timestamp and Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Timestamp</h4>
                <p className="text-gray-600">{log.timestamp.toDate().toLocaleString()}</p>
              </div>
              
              {log.metadata && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Metadata</h4>
                  <div className="space-y-1 text-gray-600">
                    {log.metadata.sessionId && (
                      <div>Session: {log.metadata.sessionId}</div>
                    )}
                    {log.metadata.ipAddress && (
                      <div>IP: {log.metadata.ipAddress}</div>
                    )}
                    {log.metadata.userAgent && (
                      <div>User Agent: {log.metadata.userAgent.substring(0, 50)}...</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Changes Details */}
            {log.changes && (
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Changes</h4>
                <div className="space-y-2">
                  {formatChanges(log.changes)}
                </div>
              </div>
            )}

            {/* Description */}
            {log.description && (
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Description</h4>
                <p className="text-gray-600 text-sm">{log.description}</p>
              </div>
            )}

            {/* Raw Data Toggle */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <button
                onClick={() => setShowRawData(!showRawData)}
                className="flex items-center space-x-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showRawData ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span>{showRawData ? 'Hide' : 'Show'} Raw Data</span>
              </button>

              <div className="flex items-center space-x-2">
                <button
                  onClick={copyToClipboard}
                  className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                >
                  <Copy className="w-3 h-3" />
                  <span>{copied ? 'Copied!' : 'Copy'}</span>
                </button>
                
                {log.resourceId && log.resource === 'sales' && (
                  <button
                    onClick={() => {
                      // Navigate to sales record - implement based on your routing
                      console.log('Navigate to sales record:', log.resourceId);
                    }}
                    className="flex items-center space-x-1 px-2 py-1 text-xs text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    <span>View Record</span>
                  </button>
                )}
              </div>
            </div>

            {/* Raw Data */}
            {showRawData && (
              <div className="mt-3">
                <h4 className="font-medium text-gray-700 mb-2">Raw Log Data</h4>
                <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-auto max-h-60">
                  {JSON.stringify(log, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogViewer;
